import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const [activeSlug, setActiveSlug] = useState(slug || 'shengli-jiegou');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: activeCategory ? `${activeCategory.name} - 女性私密健康指南` : '分类页面',
    description: activeCategory?.description || '',
    url: `https://readdy.ai/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://readdy.ai/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: activeCategory?.name || '分类' },
              ]}
            />
          </div>

          <section className="py-8 md:py-12 bg-background-100">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                  {activeCategory?.name}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                  {activeCategory?.description}
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 flex-wrap mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 text-foreground-600 border border-background-300/60 hover:border-primary-300'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    image={article.image}
                    category={article.category}
                    categoryName={activeCategory?.name || ''}
                    readTime={article.readTime}
                    publishedAt={article.publishedAt}
                  />
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}